import type { FetchDocumentContentResponse } from '@shared/types/drive';
import { Collapse, Icon, IconButton, Paper, Typography } from '@shared/ui';
import { type KeyboardEventHandler, useEffect, useState } from 'react';
import type { TimelineWidgetProps, ToolResultEntity } from '../types';
import { hasUsableDriveContent } from './driveContent';

export interface DriveDocumentToolResultWidgetProps extends TimelineWidgetProps<ToolResultEntity> {
  debugMode?: boolean;
  /**
   * When true, renders as an embedded view for inclusion inside another widget (e.g. ToolWidget).
   * This avoids nested Paper + expand/collapse UI.
   */
  embedded?: boolean;
}

// Helper to check if result is a drive_fetch_document_content response
function isDriveDocumentResult(result: any): result is FetchDocumentContentResponse {
  return (
    result &&
    typeof result === 'object' &&
    'documentId' in result &&
    typeof result.documentId === 'string'
  );
}

export function DriveDocumentToolResultWidget({
  entity,
  debugMode = false,
  embedded = false,
}: DriveDocumentToolResultWidgetProps) {
  const { result, error } = entity.props;
  const [expanded, setExpanded] = useState(debugMode);

  // Auto-expand when debug mode is turned on
  useEffect(() => {
    if (!embedded && debugMode) {
      setExpanded(true);
    }
  }, [debugMode, embedded]);

  const hasError = !!error;
  const isDriveDoc = !hasError && isDriveDocumentResult(result);
  const driveResult = isDriveDoc ? result : null;
  const isUnavailable =
    driveResult?.status === 'content_unavailable' ||
    (!!driveResult && driveResult.status !== 'success' && !hasUsableDriveContent(driveResult));
  const unavailableMessage =
    driveResult?.userMessage?.trim() || "Moments couldn't read this document content.";

  const toggleExpanded = () => setExpanded((prev) => !prev);
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  const docUrl = driveResult?.webViewUrl;
  const titleText = driveResult?.name?.trim() ? driveResult.name.trim() : 'document';

  if (embedded) {
    return (
      <div className="min-w-0">
        {error && (
          <div className="mt-2 min-w-0">
            <Typography variant="body2" color="error" className="break-words">
              {error}
            </Typography>
          </div>
        )}

        {driveResult && (
          <div className="space-y-3">
            {isUnavailable ? (
              <div className="space-y-1">
                <Typography variant="body2" color="error" className="min-w-0 truncate">
                  Could not read document <span className="font-semibold">{titleText}</span>
                </Typography>
                <Typography variant="body2" color="error" className="break-words">
                  {unavailableMessage}
                </Typography>
              </div>
            ) : (
              <Typography variant="body2" className="min-w-0 truncate">
                Fetched document <span className="font-semibold">{titleText}</span>
              </Typography>
            )}

            {typeof docUrl === 'string' && docUrl.trim() !== '' ? (
              <div className="flex items-center">
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-semibold text-white underline hover:opacity-90"
                >
                  Open document
                </a>
              </div>
            ) : null}

            {/* Debug info */}
            {debugMode && (
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                  Debug: Raw JSON
                </summary>
                <pre className="mt-2 bg-gray-100 rounded p-2 overflow-x-auto">
                  {JSON.stringify(
                    {
                      documentId: driveResult.documentId,
                      name: driveResult.name,
                      mimeType: driveResult.mimeType,
                      webViewUrl: driveResult.webViewUrl,
                      contentLength:
                        typeof driveResult.content === 'string'
                          ? driveResult.content.length
                          : undefined,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Paper
      variant="outlined"
      className={`p-3 w-full min-w-0 ${hasError || isUnavailable ? 'bg-red-50' : 'bg-blue-50'}`}
    >
      <div className="flex items-start gap-2 min-w-0">
        <Icon sx={{ fontSize: 20, color: hasError || isUnavailable ? 'error.main' : 'info.main' }}>
          {hasError || isUnavailable ? 'error_outline' : 'article'}
        </Icon>
        <div className="flex-1 min-w-0">
          <div
            className="flex items-center gap-2 cursor-pointer min-w-0"
            role="button"
            tabIndex={0}
            onClick={toggleExpanded}
            onKeyDown={handleKeyDown}
            aria-expanded={expanded}
            aria-label="Toggle document details"
          >
            <Typography variant="subtitle2" className="min-w-0 flex-shrink-0">
              {hasError
                ? '❌ Document Fetch Error'
                : isUnavailable
                  ? 'Document Unavailable'
                  : '📄 Document Fetched'}
            </Typography>
            <IconButton size="small" sx={{ ml: 'auto', flexShrink: 0 }}>
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          <Collapse in={expanded}>
            {error && (
              <div className="mt-2 min-w-0">
                <Typography variant="body2" color="error" className="break-words">
                  {error}
                </Typography>
              </div>
            )}

            {driveResult && (
              <div className="mt-3 space-y-3">
                {isUnavailable ? (
                  <div className="space-y-1">
                    <Typography variant="body2" color="error" className="min-w-0 truncate">
                      Could not read document <span className="font-semibold">{titleText}</span>
                    </Typography>
                    <Typography variant="body2" color="error" className="break-words">
                      {unavailableMessage}
                    </Typography>
                  </div>
                ) : (
                  <Typography variant="body2" className="min-w-0 truncate">
                    Fetched document <span className="font-semibold">{titleText}</span>
                  </Typography>
                )}

                {typeof docUrl === 'string' && docUrl.trim() !== '' ? (
                  <div className="flex items-center">
                    <a
                      href={docUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-semibold text-white underline hover:opacity-90"
                    >
                      Open document
                    </a>
                  </div>
                ) : null}

                {/* Debug info */}
                {debugMode && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                      Debug: Raw JSON
                    </summary>
                    <pre className="mt-2 bg-gray-100 rounded p-2 overflow-x-auto">
                      {JSON.stringify(
                        {
                          documentId: driveResult.documentId,
                          name: driveResult.name,
                          mimeType: driveResult.mimeType,
                          webViewUrl: driveResult.webViewUrl,
                          contentLength:
                            typeof driveResult.content === 'string'
                              ? driveResult.content.length
                              : undefined,
                        },
                        null,
                        2
                      )}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}

// Register this widget for drive_fetch_document_content tool results
// We'll check the tool name in the registration
export function registerDriveDocumentToolResultWidget() {
  // This will be called from registerAll.ts
  // For now, we'll enhance the existing ToolResultWidget to detect drive documents
}
